import React, { useState, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { makeStyles } from "@material-ui/core/styles/index"
import Typography from "@material-ui/core/Typography"
import PhoneInput, {
  isValidPhoneNumber,
  isSupportedCountry,
  formatPhoneNumberIntl,
} from "react-phone-number-input"
import "react-phone-number-input/style.css"
import { injectIntl, Link } from "gatsby-plugin-intl"

import {
  ACCENT,
  CTA_BUTTON_PRIMARY,
  CTA_BUTTON_SECONDARY,
  CTA_NOTE,
} from "../../styles/cta"
import {
  fetchSlots,
  requestAppointment,
  verifyAppointment,
  resendCode,
  changeMobileNumber,
  uploadPhotos,
  createLead,
  updateLead,
  getVisitorId,
  recordEvent,
  STAGE,
  isLiveBackend,
} from "./backend"
import SlotCalendar from "./SlotCalendar"
import { fireBookingConversion } from "./adsConversion"
import { normaliseDateOfBirth } from "./dateOfBirth"
import { shrinkPhoto } from "./shrinkPhoto"

const PROCEDURES = ["facelift", "nose", "other"]

const NOSE_TYPES = ["reconstruction", "primary", "revision"]

// Each "Other" answer names the appointment it leads to, grouped the way the
// price list groups them. The introductory call is deliberately absent: only
// the facelift procedure can reach it. Must match OTHER_TYPES in the booking
// Edge Function, which re-checks it.
const OTHER_GROUPS = {
  surgical: {
    browLift: "surgicalConsult",
    midfaceLift: "surgicalConsult",
    eyelids: "surgicalConsult",
    lipLift: "surgicalConsult",
    buccalFat: "surgicalConsult",
    ears: "surgicalConsult",
    scarRevision: "surgicalConsult",
    correction: "surgicalConsult",
    // Listed as surgical, but fat grafting is Dr. Schmidt's: on its own it
    // still goes to her intake.
    fatGrafting: "nonSurgicalIntake",
  },
  nonSurgical: {
    injectables: "nonSurgicalIntake",
    skin: "nonSurgicalIntake",
  },
  other: {
    somethingElse: "surgicalConsult",
  },
}
const OTHER_TYPES = Object.assign({}, ...Object.values(OTHER_GROUPS))
const NEEDS_TEXT = ["correction", "somethingElse"]

const DOCTOR_BY_ID = { 1125: "faris", 1452: "schmidt" }

// Only the facelift branch reaches the introductory call, so only it is
// qualified on price - everything else goes to a paid consultation, which
// filters on its own.
const FACELIFT_FLOOR = "€20k"

const FACELIFT = "facelift"

// Under "Other" several treatments can be chosen. Anything surgical among them
// means Dr. Faris, even alongside fat grafting; only when every choice is one
// of Dr. Schmidt's treatments does the lead go to her intake.
const routeFor = ({ procedure, noseType, otherTypes, otherText, budgetOk }) => {
  if (procedure === FACELIFT) return budgetOk === true ? "introCall" : null
  if (procedure === "nose") return noseType ? "surgicalConsult" : null
  if (procedure === "other") {
    if (!otherTypes.length) return null
    if (otherTypes.some((t) => NEEDS_TEXT.includes(t)) && !otherText.trim()) {
      return null
    }
    return otherTypes.some((t) => OTHER_TYPES[t] === "surgicalConsult")
      ? "surgicalConsult"
      : "nonSurgicalIntake"
  }
  return null
}

export const PRESETS = {
  facelift: { procedure: FACELIFT },
  nose: { procedure: "nose" },
  injectables: { procedure: "other", otherTypes: ["injectables"] },
}

const PHOTO_SLOTS = ["front", "side", "chin"]

const SEXES = ["female", "male", "other"]
const RESEND_AFTER_MS = 60 * 1000
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1)

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const DEFAULT_COUNTRY = "NL"

const hasLetter = (v) => /\p{L}/u.test(v)

// The browser's own locale is the only country signal available without calling
// out to a geo-IP service. It is read after mount so the server-rendered markup
// stays identical to the first client render.
// The time zone says where someone is; the browser language often doesn't
// (a Dutch visitor with an English-US browser was offered +1).
const ZONE_COUNTRY = {
  "Europe/Amsterdam": "NL",
  "Europe/Brussels": "BE",
  "Europe/London": "GB",
  "Europe/Dublin": "IE",
  "Europe/Berlin": "DE",
  "Europe/Paris": "FR",
  "Europe/Zurich": "CH",
  "Europe/Vienna": "AT",
  "Europe/Luxembourg": "LU",
  "Europe/Oslo": "NO",
  "Europe/Stockholm": "SE",
  "Europe/Copenhagen": "DK",
  "Europe/Madrid": "ES",
  "Europe/Rome": "IT",
  "Europe/Lisbon": "PT",
}

const detectCountry = () => {
  if (typeof navigator === "undefined") return DEFAULT_COUNTRY
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (ZONE_COUNTRY[zone]) return ZONE_COUNTRY[zone]
  } catch (e) {
    // fall through to the browser language
  }
  const tags = navigator.languages || [navigator.language]
  for (const tag of tags) {
    if (!tag) continue
    const region = tag.split("-")[1]
    if (region && isSupportedCountry(region.toUpperCase())) {
      return region.toUpperCase()
    }
  }
  return DEFAULT_COUNTRY
}

const useStyles = makeStyles(() => ({
  wrap: {
    maxWidth: 780,
    margin: 0,
    width: "100%",
    textAlign: "left",
    scrollMarginTop: 90,
  },
  intro: {
    color: "#c9c4c2",
    fontSize: "1rem",
    lineHeight: 1.6,
    marginBottom: 6,
  },
  row: {
    display: "flex",
    gap: 14,
    "@media (max-width:600px)": { flexDirection: "column", gap: 0 },
  },
  field: { position: "relative", width: "100%", marginTop: 14 },
  input: {
    fontSize: 17,
    fontWeight: 500,
    border: "1px solid #3a3a3a",
    borderRadius: 24,
    backgroundColor: "#141414",
    width: "100%",
    height: 46,
    boxSizing: "border-box",
    paddingLeft: 18,
    outline: "none",
    color: "white",
    fontFamily: "inherit",
    "&::placeholder": { color: "#8a8686", fontWeight: 400, opacity: 1 },
    "&:focus": { borderColor: ACCENT },
    "&:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px #141414 inset",
      WebkitTextFillColor: "white",
      transition: "background-color 9999s ease-out, color 9999s ease-out",
    },
  },
  message: {
    fontSize: 16,
    fontWeight: 500,
    border: "1px solid #3a3a3a",
    borderRadius: 18,
    backgroundColor: "#141414",
    color: "white",
    width: "100%",
    boxSizing: "border-box",
    minHeight: 120,
    padding: 16,
    resize: "vertical",
    outline: "none",
    fontFamily: "inherit",
    "&::placeholder": { color: "#8a8686", fontWeight: 400, opacity: 1 },
    "&:focus": { borderColor: ACCENT },
  },
  inputError: { borderColor: "#e2544f !important" },
  errorMessage: {
    display: "block",
    marginTop: 5,
    marginLeft: 16,
    fontSize: 11,
    color: "#e2544f",
  },
  phoneField: {
    marginTop: 14,
    width: "100%",
    "& .PhoneInput": { width: "100%" },
    "& .PhoneInputInput": {
      width: "100%",
      boxSizing: "border-box",
      fontSize: 17,
      fontWeight: 500,
      border: "1px solid #3a3a3a",
      borderRadius: 24,
      backgroundColor: "#141414",
      paddingLeft: 18,
      color: "white",
      height: 46,
      outline: "none",
      fontFamily: "inherit",
      "&::placeholder": { color: "#8a8686", opacity: 1 },
      "&:focus": { borderColor: ACCENT },
    },
    "& .PhoneInputCountry": {
      backgroundColor: "#141414",
      border: "1px solid #3a3a3a",
      borderRadius: 24,
      height: 46,
      paddingLeft: 15,
      paddingRight: 26,
      marginRight: 8,
      color: "white",
      position: "relative",
      cursor: "pointer",
    },
    "& .PhoneInputCountryIcon": {
      width: 24,
      height: 17,
      borderRadius: 3,
      overflow: "hidden",
      boxShadow: "0 0 0 1px rgba(0,0,0,0.12)",
      backgroundColor: "transparent",
    },
    "& .PhoneInputCountryIcon--border": {
      boxShadow: "0 0 0 1px rgba(0,0,0,0.12)",
    },
    "& .PhoneInputCountryIconImg": { display: "block", objectFit: "cover" },
    "& .PhoneInputCountrySelectArrow": {
      width: 7,
      height: 7,
      marginLeft: 10,
      borderStyle: "solid",
      borderColor: "currentColor",
      borderWidth: "0 1.5px 1.5px 0",
      borderTopWidth: 0,
      borderLeftWidth: 0,
      transform: "translateY(-2px) rotate(45deg)",
      opacity: 0.55,
    },
  },
  reveal: {
    animation: "$revealIn 380ms ease-out",
    overflow: "hidden",
  },
  "@keyframes revealIn": {
    from: { opacity: 0, transform: "translateY(-6px)" },
    to: { opacity: 1, transform: "none" },
  },
  groupLabel: {
    color: "#9d989a",
    textTransform: "uppercase",
    letterSpacing: "0.16em",
    fontSize: "0.72rem",
    fontWeight: 600,
    marginTop: 18,
    marginBottom: 10,
  },
  chips: { display: "flex", gap: 8, flexWrap: "wrap" },
  chip: {
    color: "#cfcccf",
    backgroundColor: "transparent",
    border: "1px solid #4a4a4a",
    borderRadius: 999,
    padding: "9px 18px",
    fontSize: "0.9rem",
    // 600 in BOTH states: bolding only when selected changes the text width and
    // reflows the row onto a second line the moment you tap it.
    fontWeight: 600,
    whiteSpace: "nowrap",
    fontFamily: "inherit",
    cursor: "pointer",
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    "&:hover": { borderColor: ACCENT, color: "#fff" },
    "&:focus": { outline: "none" },
    "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: 3 },
  },
  chipOn: {
    color: "#f2efe9",
    backgroundColor: "rgba(239, 234, 226, 0.09)",
    borderColor: "#b9b2a6",
  },
  budgetExit: {
    marginTop: 18,
    padding: "16px 18px",
    border: "1px solid #3a3a3a",
    borderRadius: 18,
    backgroundColor: "#141414",
    color: "#c9c4c2",
    fontSize: "0.95rem",
    lineHeight: 1.6,
  },
  actions: { marginTop: 24 },
  submit: { ...CTA_BUTTON_PRIMARY },
  secondary: { ...CTA_BUTTON_SECONDARY, marginTop: 12 },
  disabled: { opacity: 0.4, cursor: "not-allowed", pointerEvents: "none" },
  note: { ...CTA_NOTE },
  privacy: { ...CTA_NOTE, marginTop: 18 },
  privacyLink: { color: "#b7b2b0", textDecoration: "underline" },

  slotPanel: {
    scrollMarginTop: 90,
    padding: "0 26px 26px",
    border: "1px solid #2e2e2e",
    borderRadius: 22,
    backgroundColor: "#111",
    position: "relative",
    "@media (max-width:600px)": { padding: "0 16px 20px" },
  },
  stepper: {
    display: "flex",
    justifyContent: "center",
    gap: 22,
    margin: "0 -26px 22px",
    padding: "14px 16px",
    borderBottom: "1px solid #2a2a2a",
    "@media (max-width:600px)": { margin: "0 -16px 18px", gap: 14 },
  },
  step: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    color: "#8a8686",
    fontSize: "0.95rem",
    fontWeight: 500,
    background: "none",
    border: "none",
    padding: 0,
    fontFamily: "inherit",
  },
  stepOn: { color: "#f2efe9", fontWeight: 600 },
  stepBack: {
    cursor: "pointer",
    "&:hover": { color: "#f2efe9" },
    "&:focus": { outline: "none" },
    "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: 3 },
  },
  stepDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "currentColor",
  },
  preview: { marginTop: 26 },
  light: {
    "& $slotPanel": { backgroundColor: "#f7f4ef", borderColor: "#e2ddd6" },
    "& $stepper, & $confirmSection": { borderColor: "#e2ddd6" },
    "& $step": { color: "#6b6560" },
    "& $stepOn": { color: "#111" },
    "& $intro, & $photoIntro, & $doneText, & $hint, & $subLabel, & $guidance": {
      color: "#444",
    },
    "& $groupLabel": { color: "#6b6560" },
    "& $slotTitle, & $doneTitle, & $modeLabel, & $photoLabel": {
      color: "#111",
    },
    "& $held": { color: "#7a6440" },
    "& $input, & $message": {
      backgroundColor: "#fff",
      borderColor: "#cfc8bf",
      color: "#111",
      "&::placeholder": { color: "#8a8480" },
      // Browser autofill otherwise repaints the field with the dark card's
      // colours.
      "&:-webkit-autofill": {
        WebkitBoxShadow: "0 0 0 1000px #fff inset",
        WebkitTextFillColor: "#111",
      },
    },
    "& $phoneField .PhoneInputInput, & $phoneField .PhoneInputCountry": {
      backgroundColor: "#fff",
      borderColor: "#cfc8bf",
      color: "#111",
    },
    "& $phoneField .PhoneInputCountrySelectArrow": { color: "#555" },
    "& $chip, & $choice": { color: "#333", borderColor: "#cfc8bf" },
    "& $chip:hover, & $choice:hover": { color: "#111", borderColor: "#8a7f70" },
    '& $chipOn:hover, & $choice[aria-pressed="true"]:hover': {
      color: "#fff",
      borderColor: "#3a2618",
    },
    "& $input$selectEmpty": { color: "#8a8480" },
    '& $chipOn, & $choice[aria-pressed="true"]': {
      color: "#fff",
      backgroundColor: "#3a2618",
      borderColor: "#3a2618",
    },
    "& $modeBox, & $budgetExit": {
      backgroundColor: "#efe9e1",
      borderColor: "#e2ddd6",
      color: "#444",
    },
    "& $modeOption": { color: "#111", borderColor: "#b5ada2" },
    "& $price, & $note, & $privacy, & $linkBtn, & $privacyLink": {
      color: "#555",
    },
    "& $secondary": { color: "#111", borderColor: "#b5ada2" },
    "& $photoTile": { backgroundColor: "#faf8f5", borderColor: "#cfc8bf" },
  },
  choices: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    marginBottom: 14,
    "@media (max-width:760px)": { gridTemplateColumns: "1fr" },
  },
  choice: {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "13px 18px",
    border: "1px solid #4a4a4a",
    borderRadius: 16,
    background: "transparent",
    color: "#cfcccf",
    fontFamily: "inherit",
    fontSize: "1rem",
    fontWeight: 600,
    lineHeight: 1.35,
    cursor: "pointer",
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    "&:hover": { borderColor: ACCENT, color: "#fff" },
    "&:focus": { outline: "none" },
    "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: 3 },
    '&[aria-pressed="true"]': {
      color: "#f2efe9",
      backgroundColor: "rgba(239, 234, 226, 0.09)",
      borderColor: "#b9b2a6",
    },
  },
  modeBox: {
    margin: "0 0 18px",
    padding: "14px 16px 16px",
    border: "1px solid #3d3d3d",
    borderRadius: 18,
    backgroundColor: "#1a1a1a",
  },
  modeHead: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 12,
  },
  modeLabel: { color: "#fff", fontSize: "1.05rem", fontWeight: 600 },
  modeOptions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    "@media (max-width:420px)": { gridTemplateColumns: "1fr" },
  },
  modeOption: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    borderColor: "#6a6a6a",
    color: "#f2efe9",
  },
  price: { color: "#cfcccf", fontSize: "1rem" },
  otherGroup: { marginBottom: 12 },
  confirmSection: {
    scrollMarginTop: 90,
    marginTop: 24,
    paddingTop: 20,
    borderTop: "1px solid #2a2a2a",
  },
  messageSmall: { minHeight: 76, height: 76 },
  hint: { color: "#9d989a", fontSize: "0.9rem", margin: "-4px 0 10px" },
  subLabel: {
    color: "#cfcccf",
    fontSize: "0.9rem",
    fontWeight: 600,
    margin: "4px 0 8px",
  },
  slotTitle: {
    color: "white",
    fontSize: "1.05rem",
    fontWeight: 600,
    marginBottom: 4,
  },

  photoIntro: {
    color: "#c9c4c2",
    fontSize: "0.98rem",
    lineHeight: 1.65,
    marginTop: 6,
  },
  photoGrid: {
    marginTop: 18,
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 12,
    "@media (max-width:600px)": { gridTemplateColumns: "1fr" },
  },
  photoTile: {
    border: "1px dashed #3f3f3f",
    borderRadius: 18,
    backgroundColor: "#141414",
    padding: 14,
    textAlign: "center",
    cursor: "pointer",
    display: "block",
    position: "relative",
    transition: "border-color 0.15s ease",
    "&:hover": { borderColor: ACCENT },
    "&:focus-within": { outline: `2px solid ${ACCENT}`, outlineOffset: 3 },
  },
  photoTileFilled: { borderStyle: "solid", borderColor: "#b9b2a6" },
  photoDiagram: { display: "block", margin: "0 auto 8px" },
  photoLabel: {
    display: "block",
    whiteSpace: "nowrap",
    color: "#d9d5d0",
    fontSize: "0.85rem",
    fontWeight: 500,
  },
  photoAction: {
    display: "block",
    whiteSpace: "nowrap",
    color: "#8a8686",
    fontSize: "0.75rem",
    marginTop: 7,
  },
  // Visually hidden but still focusable: display:none would take the input
  // out of the tab order, and the quote path cannot be finished without it.
  photoHidden: {
    position: "absolute",
    width: 1,
    height: 1,
    opacity: 0,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
  },
  guidance: {
    marginTop: 16,
    color: "#9d989a",
    fontSize: "0.85rem",
    lineHeight: 1.6,
  },
  held: {
    marginTop: 4,
    color: "#b9b2a6",
    fontSize: "0.9rem",
    fontWeight: 500,
    letterSpacing: "0.02em",
  },
  linkBtn: {
    marginTop: 14,
    background: "none",
    border: "none",
    padding: 0,
    color: "#b7b2b0",
    textDecoration: "underline",
    fontSize: "0.85rem",
    fontFamily: "inherit",
    cursor: "pointer",
  },
  doneTitle: {
    color: "white",
    fontSize: "1.3rem",
    fontWeight: 600,
    marginBottom: 8,
  },
  doneText: { color: "#c9c4c2", fontSize: "1rem", lineHeight: 1.65 },
  dobRow: {
    display: "grid",
    gridTemplateColumns: "1fr 2fr 1.3fr",
    gap: 10,
  },
  select: {
    appearance: "none",
    WebkitAppearance: "none",
    cursor: "pointer",
  },
  selectEmpty: { color: "#8a8686", fontWeight: 400 },
  codeInput: {
    letterSpacing: "0.4em",
    fontSize: 22,
    textAlign: "center",
    paddingLeft: 0,
    maxWidth: 240,
  },
}))

const PhotoDiagram = ({ variant }) => {
  const common = { fill: "none", stroke: "#6f6a68", strokeWidth: 1.6 }
  return (
    <svg width="44" height="52" viewBox="0 0 44 52" aria-hidden="true">
      {variant === "front" && (
        <>
          <ellipse cx="22" cy="22" rx="12" ry="15" {...common} />
          <circle cx="17" cy="20" r="1.6" fill="#6f6a68" stroke="none" />
          <circle cx="27" cy="20" r="1.6" fill="#6f6a68" stroke="none" />
          <path d="M17 29h10" {...common} />
          <path d="M10 44c2-6 20-6 24 0" {...common} />
        </>
      )}
      {variant === "side" && (
        <>
          <path
            d="M28 8c-9 0-15 6-15 14 0 5 2 7 2 10 0 3-3 3-3 5 0 2 4 2 6 2"
            {...common}
          />
          <path d="M28 8c7 2 9 8 9 14s-3 12-9 14" {...common} />
          <circle cx="30" cy="22" r="1.6" fill="#6f6a68" stroke="none" />
          <path d="M12 44c3-5 18-5 22 0" {...common} />
        </>
      )}
      {variant === "chin" && (
        <>
          <path
            d="M28 10c-9 0-15 6-15 14 0 5 2 7 2 10 0 3-3 4-3 6"
            {...common}
          />
          <path d="M28 10c7 2 9 8 9 14s-4 13-11 16" {...common} />
          <circle cx="30" cy="23" r="1.6" fill="#6f6a68" stroke="none" />
          <path d="M30 40l-4 5" {...common} strokeWidth="1.2" />
          <path d="M26 45l3-1M26 45l1-3" {...common} strokeWidth="1.2" />
        </>
      )}
    </svg>
  )
}
PhotoDiagram.propTypes = { variant: PropTypes.string.isRequired }

const iconProps = {
  width: 22,
  height: 22,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
  style: { flexShrink: 0 },
}

const VideoIcon = () => (
  <svg {...iconProps}>
    <rect x="2.5" y="6" width="13" height="12" rx="2.5" />
    <path d="M15.5 10.5l6-3.5v10l-6-3.5z" />
  </svg>
)

const PersonIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="8" r="3.6" />
    <path d="M4.5 20.5c1.2-4 4-6 7.5-6s6.3 2 7.5 6" />
  </svg>
)

const PhotoDone = () => (
  <svg width="44" height="52" viewBox="0 0 44 52" aria-hidden="true">
    <circle
      cx="22"
      cy="26"
      r="13"
      fill="none"
      stroke="#b9b2a6"
      strokeWidth="1.6"
    />
    <path
      d="M15.5 26.5l4.5 4.5 8.5-9.5"
      fill="none"
      stroke="#b9b2a6"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const LeadIntakeForm = ({
  intl,
  selected = [],
  selectedAreas = [],
  onToggleArea,
  allSelected = false,
  onToggleAll,
  preset,
  withQuote = true,
  theme = "light",
}) => {
  const t = (id) => intl.formatMessage({ id })
  const classes = useStyles()
  const initial = PRESETS[preset] || {}

  const [state, setState] = useState({
    phone: "",
    firstName: "",
    lastName: "",
    email: "",
  })
  const [country, setCountry] = useState(DEFAULT_COUNTRY)
  const [procedure, setProcedure] = useState(initial.procedure || "")
  const [noseType, setNoseType] = useState(null)
  const [otherTypes, setOtherTypes] = useState(initial.otherTypes || [])
  const [otherText, setOtherText] = useState("")
  const [budgetOk, setBudgetOk] = useState(null)
  const [touched, setTouched] = useState({})

  const [intent, setIntent] = useState(null) // "quote" | "call"
  const [stage, setStage] = useState("form") // form | slots | photos | done
  const [slots, setSlots] = useState(null) // null = not fetched yet
  const [chosenSlot, setChosenSlot] = useState(null)
  const [sex, setSex] = useState(null)
  const [dob, setDob] = useState({ day: "", month: "", year: "" })
  const [bookingMessage, setBookingMessage] = useState("")
  const [dobTouched, setDobTouched] = useState(false)
  const [requestId, setRequestId] = useState(null)
  const [code, setCode] = useState("")
  const [codeError, setCodeError] = useState(false)
  const [resendAt, setResendAt] = useState(0)
  const [resendUsed, setResendUsed] = useState(false)
  const [resent, setResent] = useState(false)
  const [editingPhone, setEditingPhone] = useState(false)
  const [newPhone, setNewPhone] = useState("")
  const [deposit, setDeposit] = useState(null)
  const [now, setNow] = useState(0)
  const [photos, setPhotos] = useState({ front: null, side: null, chin: null })
  const [shrinking, setShrinking] = useState(0)
  const [busy, setBusy] = useState(false)
  const [consultMode, setConsultMode] = useState("video")
  // The route the server actually offered: a facelift lead can be moved to
  // Dr. Faris when Dr. Schmidt has no intro call within a month.
  const [bookingRoute, setBookingRoute] = useState(null)
  // Facelift leads choose on the calendar screen between the intro call and
  // Dr. Faris's consultation. The choice is only offered while Dr. Schmidt has
  // intro calls in the coming month.
  const [firstStep, setFirstStep] = useState("introCall")
  const [introOffered, setIntroOffered] = useState(false)
  const [failed, setFailed] = useState(false)
  const slotPanelRef = React.useRef(null)
  const rootRef = React.useRef(null)
  const confirmRef = React.useRef(null)
  const visitorRef = React.useRef(null)
  const conversionSent = React.useRef(false)
  const [leadId, setLeadId] = useState(null)

  const update = (key) => (value) =>
    setState((prev) => ({ ...prev, [key]: value }))

  useEffect(() => {
    setCountry(detectCountry())
    visitorRef.current = getVisitorId()
  }, [])

  const track = (type, data) => {
    if (visitorRef.current) recordEvent(visitorRef.current, type, data)
  }

  // The diary sits below the form, so unlocking it off-screen leaves the patient
  // looking at the form they just finished.
  useEffect(() => {
    if (stage === "form") return
    const target = rootRef.current
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" })
  }, [stage])

  useEffect(() => {
    if (stage === "slots" && chosenSlot && confirmRef.current) {
      confirmRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [chosenSlot, stage])

  const emailValid = emailRegex.test(state.email)
  const phoneValid = Boolean(state.phone) && isValidPhoneNumber(state.phone)
  const firstNameValid = hasLetter(state.firstName)
  const lastNameValid = hasLetter(state.lastName)
  const identityComplete = phoneValid && firstNameValid && lastNameValid
  const needsBudget = procedure === FACELIFT
  const budgetBlocked = needsBudget && budgetOk === false
  const hasAreaPicker = Boolean(onToggleArea)
  const route = routeFor({
    procedure,
    noseType,
    otherTypes,
    otherText,
    budgetOk,
  })

  const complete =
    identityComplete &&
    emailValid &&
    Boolean(route) &&
    (procedure !== FACELIFT || !hasAreaPicker || selected.length > 0)

  const lead = useMemo(
    () => ({
      ...state,
      procedure,
      noseType: procedure === "nose" ? noseType : undefined,
      otherTypes: procedure === "other" ? otherTypes : [],
      otherText: otherTypes.some((t) => NEEDS_TEXT.includes(t))
        ? otherText
        : undefined,
      areas: procedure === FACELIFT ? selectedAreas : [],
      budgetConfirmed: needsBudget ? budgetOk === true : undefined,
      route,
      intent,
    }),
    [
      state,
      procedure,
      noseType,
      otherTypes,
      otherText,
      selectedAreas,
      budgetOk,
      needsBudget,
      route,
      intent,
    ]
  )

  // The form stays visible and editable above the diary, so anything that
  // invalidates it has to close the diary again - otherwise a lead who answers
  // "that's above my budget" after unlocking it can still take a slot.
  const resetToForm = () => {
    setStage("form")
    setSlots(null)
    setChosenSlot(null)
    setConsultMode("video")
    setBookingRoute(null)
  }

  const loadSlots = async (forRoute, mode, step = firstStep) => {
    setSlots(null)
    setChosenSlot(null)
    setFailed(false)
    try {
      const res = await fetchSlots(forRoute, mode, step)
      setBookingRoute(res.route || forRoute)
      setIntroOffered(forRoute === "introCall" && res.noSchmidtCalls === false)
      setSlots(res.slots)
    } catch (e) {
      setFailed(true)
    }
  }

  useEffect(() => {
    if (stage === "slots" && !complete) resetToForm()
  }, [stage, complete])

  const start = async (which) => {
    if (!complete || busy) return
    setIntent(which)
    setBusy(true)
    setFailed(false)
    try {
      const created = await createLead(visitorRef.current, {
        ...lead,
        intent: which,
        stage: STAGE.DETAILS_ONLY,
      })
      setLeadId(created.leadId)
      if (which === "quote") {
        setStage("photos")
        return
      }
      setStage("slots")
      await loadSlots(route, consultMode)
    } catch (e) {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  const chooseConsultMode = (mode) => {
    setConsultMode(mode)
    track("consultMode", { value: mode })
    loadSlots(route, mode)
  }

  const chooseFirstStep = (step) => {
    setFirstStep(step)
    track("firstStep", { value: step })
    loadSlots(route, consultMode, step)
  }

  const pickSlot = async (slot) => {
    if (busy) return
    setBusy(true)
    setFailed(false)
    try {
      setChosenSlot(slot)
      await updateLead(leadId, { slotStart: slot.start })
    } catch (e) {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  const dateOfBirth = normaliseDateOfBirth(dob)
  const bookingDetailsReady = Boolean(sex) && Boolean(dateOfBirth)

  const sendCode = async () => {
    if (!bookingDetailsReady || busy) return
    setBusy(true)
    setFailed(false)
    try {
      const res = await requestAppointment(leadId, {
        route: bookingRoute,
        consultMode:
          bookingRoute === "surgicalConsult" ? consultMode : undefined,
        start: chosenSlot.start,
        locationUuid: chosenSlot.locationUuid,
        sex,
        dateOfBirth,
        mobile: state.phone,
        message: bookingMessage.trim() || undefined,
      })
      setRequestId(res.requestId)
      setResendAt(Date.now() + RESEND_AFTER_MS)
      setResendUsed(false)
      setStage("code")
    } catch (e) {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    if (stage !== "done" && stage !== "deposit") return
    if (!isLiveBackend || conversionSent.current) return
    conversionSent.current = true
    fireBookingConversion(state)
  }, [stage, state])

  useEffect(() => {
    if (stage !== "code") return undefined
    setNow(Date.now())
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [stage])

  const verify = async () => {
    if (code.length !== 6 || busy) return
    setBusy(true)
    setFailed(false)
    setCodeError(false)
    try {
      const res = await verifyAppointment(requestId, code, bookingRoute)
      if (!res.ok) {
        setCodeError(true)
        return
      }
      await updateLead(leadId, {
        stage: res.prepaymentRequired
          ? STAGE.AWAITING_DEPOSIT
          : STAGE.CONFIRMED,
      })
      if (res.prepaymentRequired) {
        setDeposit({ amount: res.prepaymentAmount, url: res.prepaymentUrl })
        setStage("deposit")
        return
      }
      setStage("done")
    } catch (e) {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  const resend = async () => {
    if (resendUsed || now < resendAt || busy) return
    setBusy(true)
    try {
      await resendCode(requestId)
      setResendUsed(true)
      setResent(true)
    } catch (e) {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  // A corrected number is sent a fresh code and earns another resend.
  const saveNewPhone = async () => {
    if (!newPhone || !isValidPhoneNumber(newPhone) || busy) return
    setBusy(true)
    try {
      await changeMobileNumber(requestId, newPhone)
      update("phone")(newPhone)
      setEditingPhone(false)
      setResendUsed(false)
      setResent(false)
      setResendAt(Date.now() + RESEND_AFTER_MS)
      setCode("")
      setCodeError(false)
    } catch (e) {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  const photosRequired = intent === "quote"
  const photosReady = !photosRequired || PHOTO_SLOTS.every((k) => photos[k])

  // Call path only: the slot is already held, photos are optional.
  const confirm = async () => {
    if (busy || shrinking) return
    setBusy(true)
    try {
      if (PHOTO_SLOTS.some((k) => photos[k])) {
        await uploadPhotos(leadId, photos)
      }
      await updateLead(leadId, { stage: STAGE.CONFIRMED })
      setStage("done")
    } finally {
      setBusy(false)
    }
  }

  // Quote path: photos are the price of the quote, so they come before the diary.
  const toSlots = async () => {
    if (!photosReady || busy || shrinking) return
    setBusy(true)
    try {
      await uploadPhotos(leadId, photos)
      await updateLead(leadId, { stage: STAGE.PHOTOS_RECEIVED })
      setStage("slots")
      await loadSlots(route, consultMode)
    } catch (e) {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  // British order ("Tuesday 20 October"), matching the calendar.
  const fmtDay = (iso) =>
    new Intl.DateTimeFormat(intl.locale === "en" ? "en-GB" : intl.locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(new Date(iso))
  const fmtTime = (iso) =>
    new Intl.DateTimeFormat(intl.locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date(iso))

  const chip = (on) => `${classes.chip} ${on ? classes.chipOn : ""}`

  const stepper = (step) => (
    <div className={classes.stepper}>
      {step === 1 ? (
        <span className={`${classes.step} ${classes.stepOn}`}>
          <span className={classes.stepDot} />
          {t("booking.step.details")}
        </span>
      ) : (
        <button
          type="button"
          className={`${classes.step} ${
            stage === "slots" ? classes.stepBack : ""
          }`}
          onClick={() => stage === "slots" && resetToForm()}
          disabled={stage !== "slots"}
        >
          ✓ {t("booking.step.details")}
        </button>
      )}
      <span className={`${classes.step} ${step === 2 ? classes.stepOn : ""}`}>
        <span className={classes.stepDot} />
        {t("booking.step.time")}
      </span>
    </div>
  )

  const slotLabel =
    chosenSlot &&
    [fmtDay(chosenSlot.start), fmtTime(chosenSlot.start), chosenSlot.location]
      .filter(Boolean)
      .join(" · ")

  const shownRoute = bookingRoute || route

  // The first open slot says whose diary it is; the route is only a fallback.
  const bookedUntil = (date, slot) => {
    const doctor =
      (slot && DOCTOR_BY_ID[slot.userId]) ||
      (shownRoute === "surgicalConsult" ? "faris" : "schmidt")
    return intl.formatMessage({ id: `booking.bookedUntil.${doctor}` }, { date })
  }

  const backToTimes = () => {
    setChosenSlot(null)
    if (slotPanelRef.current) {
      slotPanelRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      })
    }
  }

  const confirmFields = (
    <div className={classes.confirmSection} ref={confirmRef}>
      <Typography className={classes.held}>{slotLabel}</Typography>
      <Typography className={classes.slotTitle}>
        {t("booking.confirmTitle")}
      </Typography>
      <Typography className={classes.photoIntro}>
        {t("booking.confirmIntro")}
      </Typography>

      <Typography className={classes.groupLabel}>
        {t("booking.sexLabel")}
      </Typography>
      <div className={classes.chips}>
        {SEXES.map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={sex === key}
            className={chip(sex === key)}
            onClick={() => setSex(key)}
          >
            {t(`booking.sex.${key}`)}
          </button>
        ))}
      </div>

      <Typography className={classes.groupLabel}>
        {t("booking.dobLabel")}
      </Typography>
      <div className={classes.dobRow}>
        <input
          className={classes.input}
          inputMode="numeric"
          maxLength={2}
          placeholder={t("booking.dobDay")}
          aria-label={t("booking.dobDay")}
          autoComplete="bday-day"
          value={dob.day}
          onChange={(e) =>
            setDob((p) => ({
              ...p,
              day: e.target.value.replace(/\D/g, ""),
            }))
          }
        />
        <select
          className={`${classes.input} ${classes.select} ${
            dob.month ? "" : classes.selectEmpty
          }`}
          aria-label={t("booking.dobMonth")}
          autoComplete="bday-month"
          value={dob.month}
          onChange={(e) => setDob((p) => ({ ...p, month: e.target.value }))}
        >
          <option value="">{t("booking.dobMonth")}</option>
          {MONTHS.map((m) => (
            <option key={m} value={m}>
              {new Intl.DateTimeFormat(intl.locale, {
                month: "long",
              }).format(new Date(2000, m - 1, 1))}
            </option>
          ))}
        </select>
        <input
          className={classes.input}
          inputMode="numeric"
          maxLength={4}
          placeholder={t("booking.dobYear")}
          aria-label={t("booking.dobYear")}
          autoComplete="bday-year"
          value={dob.year}
          onChange={(e) =>
            setDob((p) => ({
              ...p,
              year: e.target.value.replace(/\D/g, ""),
            }))
          }
          onBlur={() => setDobTouched(true)}
        />
      </div>
      {dobTouched && dob.day && dob.month && dob.year && !dateOfBirth && (
        <span className={classes.errorMessage}>{t("booking.dobInvalid")}</span>
      )}

      <Typography className={classes.groupLabel}>
        {t("booking.messageLabel")}
      </Typography>
      <textarea
        className={`${classes.message} ${classes.messageSmall}`}
        placeholder={t("booking.messagePlaceholder")}
        aria-label={t("booking.messageLabel")}
        maxLength={1000}
        value={bookingMessage}
        onChange={(e) => setBookingMessage(e.target.value)}
      />

      <button
        type="button"
        onClick={sendCode}
        className={`${classes.submit} ${
          bookingDetailsReady && !busy ? "" : classes.disabled
        }`}
        style={{ marginTop: 24 }}
      >
        {t("booking.sendCode")}
      </button>
      <Typography className={classes.note}>
        {intl.formatMessage(
          { id: "booking.codeNote" },
          { phone: formatPhoneNumberIntl(state.phone) || state.phone }
        )}
      </Typography>
      <button type="button" className={classes.linkBtn} onClick={backToTimes}>
        {t("booking.changeTime")}
      </button>
    </div>
  )

  if (stage === "code") {
    const secondsLeft = Math.max(0, Math.ceil((resendAt - now) / 1000))
    return (
      <div
        className={`${classes.wrap} ${theme === "light" ? classes.light : ""}`}
        ref={rootRef}
      >
        <div className={classes.slotPanel}>
          {stepper(2)}
          <Typography className={classes.held}>{slotLabel}</Typography>
          <Typography className={classes.slotTitle}>
            {intl.formatMessage(
              { id: "booking.codeTitle" },
              { phone: formatPhoneNumberIntl(state.phone) || state.phone }
            )}
          </Typography>
          <div className={classes.field}>
            <input
              className={`${classes.input} ${classes.codeInput} ${
                codeError ? classes.inputError : ""
              }`}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              aria-label={t("booking.codeLabel")}
              placeholder="••••••"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                setCodeError(false)
              }}
            />
            {codeError && (
              <span className={classes.errorMessage}>
                {t("booking.codeInvalid")}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={verify}
            className={`${classes.submit} ${
              code.length === 6 && !busy ? "" : classes.disabled
            }`}
            style={{ marginTop: 20 }}
          >
            {t(
              bookingRoute === "introCall"
                ? "booking.verify"
                : "booking.verifyWithDeposit"
            )}
          </button>

          {resent ? (
            <Typography className={classes.note}>
              {t("booking.resent")}
            </Typography>
          ) : secondsLeft > 0 ? (
            <Typography className={classes.note}>
              {intl.formatMessage(
                { id: "booking.resendWait" },
                { seconds: secondsLeft }
              )}
            </Typography>
          ) : (
            !resendUsed && (
              <button
                type="button"
                className={classes.linkBtn}
                onClick={resend}
              >
                {t("booking.resend")}
              </button>
            )
          )}

          {editingPhone ? (
            <>
              <div className={classes.phoneField}>
                <PhoneInput
                  international
                  defaultCountry={country}
                  value={newPhone}
                  onChange={(v) => setNewPhone(v || "")}
                />
              </div>
              <button
                type="button"
                onClick={saveNewPhone}
                className={`${classes.secondary} ${
                  newPhone && isValidPhoneNumber(newPhone) && !busy
                    ? ""
                    : classes.disabled
                }`}
              >
                {t("booking.saveNumber")}
              </button>
            </>
          ) : (
            <div>
              <button
                type="button"
                className={classes.linkBtn}
                onClick={() => {
                  setNewPhone(state.phone)
                  setEditingPhone(true)
                }}
              >
                {t("booking.changeNumber")}
              </button>
            </div>
          )}
          {failed && (
            <Typography className={classes.note} style={{ color: "#e2544f" }}>
              {t("booking.failed")}
            </Typography>
          )}
        </div>
      </div>
    )
  }

  if (stage === "deposit") {
    const amount = new Intl.NumberFormat(intl.locale, {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(deposit.amount)
    return (
      <div
        className={`${classes.wrap} ${theme === "light" ? classes.light : ""}`}
        ref={rootRef}
      >
        <div className={classes.slotPanel}>
          {stepper(2)}
          <Typography className={classes.held}>{slotLabel}</Typography>
          <Typography className={classes.slotTitle}>
            {t("booking.depositTitle")}
          </Typography>
          <Typography className={classes.photoIntro}>
            {intl.formatMessage({ id: "booking.depositText" }, { amount })}
          </Typography>
          <a
            href={deposit.url}
            className={classes.submit}
            style={{ marginTop: 22 }}
          >
            {t("booking.payDeposit")}
          </a>
          <Typography className={classes.note}>
            {t("booking.depositNote")}
          </Typography>
        </div>
      </div>
    )
  }

  if (stage === "done") {
    return (
      <div
        className={`${classes.wrap} ${theme === "light" ? classes.light : ""}`}
        ref={rootRef}
      >
        <div className={classes.slotPanel}>
          {stepper(2)}
          <Typography className={classes.doneTitle}>
            {t(
              shownRoute === "introCall"
                ? "internationalPatients.intake.doneTitle"
                : `booking.done.${shownRoute}`
            )}
          </Typography>
          <Typography className={classes.doneText}>
            {chosenSlot &&
              `${fmtDay(chosenSlot.start)} · ${fmtTime(chosenSlot.start)}`}
          </Typography>
          <Typography className={classes.doneText} style={{ marginTop: 10 }}>
            {t(
              intent === "quote"
                ? "internationalPatients.intake.doneTextQuote"
                : "internationalPatients.intake.doneTextCall"
            )}
          </Typography>
          {intent === "call" &&
            shownRoute === "introCall" &&
            !PHOTO_SLOTS.every((k) => photos[k]) && (
              <>
                <button
                  type="button"
                  className={classes.secondary}
                  style={{ marginTop: 20 }}
                  onClick={() => setStage("photos")}
                >
                  {t("internationalPatients.intake.addPhotos")}
                </button>
                <Typography className={classes.note}>
                  {t("internationalPatients.intake.addPhotosNote")}
                </Typography>
              </>
            )}
        </div>
      </div>
    )
  }

  if (stage === "photos") {
    const ready = photosReady && !shrinking
    return (
      <div
        className={`${classes.wrap} ${theme === "light" ? classes.light : ""}`}
        ref={rootRef}
      >
        <div className={classes.slotPanel}>
          {stepper(2)}
          <Typography className={classes.held}>
            {chosenSlot &&
              `${fmtDay(chosenSlot.start)} · ${fmtTime(chosenSlot.start)}`}
          </Typography>
          <Typography className={classes.slotTitle}>
            {t(
              photosRequired
                ? "internationalPatients.intake.photosTitle"
                : "internationalPatients.intake.photosTitleOptional"
            )}
          </Typography>
          <Typography className={classes.photoIntro}>
            {t(
              photosRequired
                ? "internationalPatients.intake.photosIntro"
                : "internationalPatients.intake.photosIntroOptional"
            )}
          </Typography>

          <div className={classes.photoGrid}>
            {PHOTO_SLOTS.map((key) => (
              <label
                key={key}
                className={`${classes.photoTile} ${
                  photos[key] ? classes.photoTileFilled : ""
                }`}
              >
                <span className={classes.photoDiagram}>
                  {photos[key] ? <PhotoDone /> : <PhotoDiagram variant={key} />}
                </span>
                <span className={classes.photoLabel}>
                  {t(`internationalPatients.intake.photo.${key}`)}
                </span>
                <span className={classes.photoAction}>
                  {t(
                    photos[key]
                      ? "internationalPatients.intake.photoAdded"
                      : "internationalPatients.intake.photoAdd"
                  )}
                </span>
                {/* No capture attribute: the OS then offers camera and
                    library together, and many patients already have photos. */}
                <input
                  type="file"
                  accept="image/*"
                  className={classes.photoHidden}
                  onChange={async (e) => {
                    const file = e.target.files && e.target.files[0]
                    if (!file) return
                    setShrinking((n) => n + 1)
                    try {
                      const photo = await shrinkPhoto(file)
                      setPhotos((prev) => ({ ...prev, [key]: photo }))
                    } finally {
                      setShrinking((n) => n - 1)
                    }
                  }}
                />
              </label>
            ))}
          </div>

          <Typography className={classes.guidance}>
            {t("internationalPatients.intake.photosGuidance")}
          </Typography>

          <button
            type="button"
            onClick={photosRequired ? toSlots : confirm}
            className={`${classes.submit} ${ready ? "" : classes.disabled}`}
            style={{ marginTop: 22 }}
          >
            {t(
              busy
                ? "internationalPatients.intake.photosSending"
                : photosRequired
                ? "internationalPatients.intake.photosSubmitQuote"
                : "internationalPatients.intake.photosSubmit"
            )}
          </button>
          {!photosRequired && (
            <Typography className={classes.note}>
              {t("internationalPatients.intake.holdNoteOptional")}
            </Typography>
          )}
        </div>
      </div>
    )
  }

  const showQuote = withQuote && (!procedure || procedure === FACELIFT)

  return (
    <div
      className={`${classes.wrap} ${theme === "light" ? classes.light : ""}`}
      ref={rootRef}
    >
      <div className={classes.slotPanel}>
        {stepper(stage === "form" ? 1 : 2)}
        {stage === "form" ? (
          <>
            <Typography className={classes.intro}>
              {t(
                withQuote
                  ? "internationalPatients.intake.intro2"
                  : "booking.intro"
              )}
            </Typography>

            <div className={classes.phoneField}>
              <PhoneInput
                key={country}
                international
                defaultCountry={country}
                value={state.phone}
                onChange={(v) => update("phone")(v || "")}
                onBlur={() => setTouched((prev) => ({ ...prev, phone: true }))}
                placeholder={t("internationalPatients.quoteForm.phone")}
              />
              {touched.phone && !phoneValid && (
                <span className={classes.errorMessage}>
                  {t("internationalPatients.intake.invalidPhone")}
                </span>
              )}
            </div>

            <div className={classes.row}>
              <div className={classes.field}>
                <input
                  className={classes.input}
                  autoComplete="given-name"
                  placeholder={`${t(
                    "internationalPatients.quoteForm.firstName"
                  )} *`}
                  value={state.firstName}
                  onChange={(e) => update("firstName")(e.target.value)}
                />
              </div>
              <div className={classes.field}>
                <input
                  className={classes.input}
                  autoComplete="family-name"
                  placeholder={`${t(
                    "internationalPatients.quoteForm.lastName"
                  )} *`}
                  value={state.lastName}
                  onChange={(e) => update("lastName")(e.target.value)}
                />
              </div>
            </div>

            {identityComplete && (
              <div className={classes.reveal}>
                <div className={classes.field}>
                  <input
                    className={`${classes.input} ${
                      touched.email && !emailValid ? classes.inputError : ""
                    }`}
                    autoComplete="email"
                    placeholder={`${t(
                      "internationalPatients.quoteForm.email"
                    )} *`}
                    value={state.email}
                    onChange={(e) => update("email")(e.target.value)}
                    onBlur={() => setTouched((p) => ({ ...p, email: true }))}
                  />
                  {touched.email && !emailValid && (
                    <span className={classes.errorMessage}>
                      {t("internationalPatients.quoteForm.invalidEmail")}
                    </span>
                  )}
                </div>

                <Typography className={classes.groupLabel}>
                  {t("internationalPatients.intake.procedureLabel")}
                </Typography>
                <div className={classes.chips}>
                  {PROCEDURES.map((key) => (
                    <button
                      key={key}
                      type="button"
                      aria-pressed={procedure === key}
                      className={chip(procedure === key)}
                      onClick={() => {
                        setProcedure(key)
                        setBudgetOk(null)
                        setNoseType(null)
                        setOtherTypes([])
                        track("procedure", { value: key })
                        if (stage !== "form") resetToForm()
                      }}
                    >
                      {t(`booking.topic.${key}`)}
                    </button>
                  ))}
                </div>

                {procedure === "nose" && (
                  <>
                    <Typography className={classes.groupLabel}>
                      {t("booking.noseLabel")}
                    </Typography>
                    <div className={classes.chips}>
                      {NOSE_TYPES.map((key) => (
                        <button
                          key={key}
                          type="button"
                          aria-pressed={noseType === key}
                          className={chip(noseType === key)}
                          onClick={() => {
                            setNoseType(key)
                            track("noseType", { value: key })
                            if (stage !== "form") resetToForm()
                          }}
                        >
                          {t(`booking.nose.${key}`)}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {procedure === "other" && (
                  <>
                    <Typography className={classes.groupLabel}>
                      {t("booking.otherLabel")}
                    </Typography>
                    <Typography className={classes.hint}>
                      {t("booking.otherHint")}
                    </Typography>
                    {Object.entries(OTHER_GROUPS).map(([group, types]) => (
                      <div key={group} className={classes.otherGroup}>
                        {group !== "other" && (
                          <Typography className={classes.subLabel}>
                            {t(`booking.otherGroup.${group}`)}
                          </Typography>
                        )}
                        <div className={classes.chips}>
                          {Object.keys(types).map((key) => (
                            <button
                              key={key}
                              type="button"
                              aria-pressed={otherTypes.includes(key)}
                              className={chip(otherTypes.includes(key))}
                              onClick={() => {
                                const next = otherTypes.includes(key)
                                  ? otherTypes.filter((k) => k !== key)
                                  : [...otherTypes, key]
                                setOtherTypes(next)
                                track("otherTypes", { values: next })
                                if (stage !== "form") resetToForm()
                              }}
                            >
                              {t(`booking.other.${key}`)}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    {otherTypes.some((k) => NEEDS_TEXT.includes(k)) && (
                      <div className={classes.field}>
                        <textarea
                          className={classes.message}
                          placeholder={t(
                            otherTypes.includes("correction") &&
                              !otherTypes.includes("somethingElse")
                              ? "booking.correctionPlaceholder"
                              : "booking.otherPlaceholder"
                          )}
                          value={otherText}
                          onChange={(e) => setOtherText(e.target.value)}
                        />
                      </div>
                    )}
                  </>
                )}

                {procedure === FACELIFT && hasAreaPicker && (
                  <>
                    <Typography className={classes.groupLabel}>
                      {t("internationalPatients.intake.areasLabel")}
                    </Typography>
                    <div className={classes.chips}>
                      <button
                        type="button"
                        aria-pressed={allSelected}
                        className={chip(allSelected)}
                        onClick={() => onToggleAll && onToggleAll()}
                      >
                        {t("internationalPatients.packages.panfacialChip")}
                      </button>
                      {["upper", "middle", "lower"].map((key) => (
                        <button
                          key={key}
                          type="button"
                          aria-pressed={selected.includes(key)}
                          className={chip(selected.includes(key))}
                          onClick={() => onToggleArea && onToggleArea(key)}
                        >
                          {t(`internationalPatients.packages.${key}.label`)}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {needsBudget && (
                  <>
                    <Typography className={classes.groupLabel}>
                      {t("internationalPatients.intake.budgetLabel")}
                    </Typography>
                    <Typography className={classes.intro}>
                      {intl.formatMessage(
                        {
                          id: "internationalPatients.intake.budgetQuestion.facelift",
                        },
                        { floor: FACELIFT_FLOOR }
                      )}
                    </Typography>
                    <div className={classes.chips} style={{ marginTop: 10 }}>
                      <button
                        type="button"
                        aria-pressed={budgetOk === true}
                        className={chip(budgetOk === true)}
                        onClick={() => {
                          setBudgetOk(true)
                          track("budget", { answer: "yes" })
                        }}
                      >
                        {t("internationalPatients.intake.budgetYes")}
                      </button>
                      <button
                        type="button"
                        aria-pressed={budgetOk === false}
                        className={chip(budgetOk === false)}
                        onClick={() => {
                          setBudgetOk(false)
                          track("budget", { answer: "no" })
                        }}
                      >
                        {t("internationalPatients.intake.budgetNo")}
                      </button>
                    </div>
                  </>
                )}

                <div className={classes.actions}>
                  {stage !== "form" ? null : budgetBlocked ? (
                    <Typography className={classes.budgetExit}>
                      {t("internationalPatients.intake.budgetExit")}
                    </Typography>
                  ) : (
                    <>
                      {showQuote && (
                        <button
                          type="button"
                          onClick={() => start("quote")}
                          className={`${classes.submit} ${
                            complete ? "" : classes.disabled
                          }`}
                        >
                          {t("internationalPatients.intake.submitQuote")}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => start("call")}
                        className={`${
                          showQuote ? classes.secondary : classes.submit
                        } ${complete ? "" : classes.disabled}`}
                      >
                        {t(
                          withQuote && (procedure === FACELIFT || !procedure)
                            ? "internationalPatients.intake.submitCall"
                            : "booking.continue"
                        )}
                      </button>
                    </>
                  )}
                </div>

                <Typography className={classes.privacy}>
                  {t("internationalPatients.quoteForm.privacyMessage")}
                  <Link to="/privacy-policy/" className={classes.privacyLink}>
                    {t("internationalPatients.quoteForm.privacyLink")}
                  </Link>
                  .
                </Typography>
              </div>
            )}

            {!identityComplete && (
              <div className={classes.preview}>
                <SlotCalendar
                  light={theme === "light"}
                  selectedId={chosenSlot ? chosenSlot.id : null}
                  locked
                  locale={intl.locale}
                  slots={slots}
                  onPick={pickSlot}
                  lockedMessage={t("internationalPatients.intake.slotsLocked")}
                  monthFullMessage={t("booking.monthFull")}
                  bookedUntilMessage={bookedUntil}
                  noTimesMessage={t("booking.noTimes")}
                  firstAvailableLabel={t("booking.firstAvailable")}
                  chooseDayMessage={t("booking.chooseDay")}
                  zoneLabel={t("internationalPatients.intake.zoneLabel")}
                  zoneSearchLabel={t("internationalPatients.intake.zoneSearch")}
                  zoneNoResults={t(
                    "internationalPatients.intake.zoneNoResults"
                  )}
                  prevLabel={t("internationalPatients.intake.calendarPrev")}
                  nextLabel={t("internationalPatients.intake.calendarNext")}
                />
              </div>
            )}
          </>
        ) : (
          <div ref={slotPanelRef}>
            {introOffered && (
              <div className={classes.choices}>
                {["introCall", "faris"].map((key) => (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={firstStep === key}
                    className={classes.choice}
                    onClick={() => chooseFirstStep(key)}
                  >
                    {t(`booking.firstStep.${key}`)}
                  </button>
                ))}
              </div>
            )}
            {shownRoute === "surgicalConsult" && (
              <>
                {!introOffered && (
                  <Typography className={classes.slotTitle}>
                    {t("booking.title.surgicalConsult")}
                  </Typography>
                )}
                <div className={classes.modeBox}>
                  <div className={classes.modeHead}>
                    <Typography className={classes.modeLabel}>
                      {t("booking.modeQuestion")}
                    </Typography>
                    <span className={classes.price}>
                      {t("booking.farisPrice")}
                    </span>
                  </div>
                  <div className={classes.modeOptions}>
                    {["video", "clinic"].map((key) => (
                      <button
                        key={key}
                        type="button"
                        aria-pressed={consultMode === key}
                        className={`${classes.choice} ${classes.modeOption}`}
                        onClick={() => chooseConsultMode(key)}
                      >
                        {key === "video" ? <VideoIcon /> : <PersonIcon />}
                        {t(`booking.mode.${key}`)}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            {
              <SlotCalendar
                light={theme === "light"}
                selectedId={chosenSlot ? chosenSlot.id : null}
                locked={slots === null}
                locale={intl.locale}
                slots={slots}
                onPick={pickSlot}
                lockedMessage={t("internationalPatients.intake.slotsLocked")}
                monthFullMessage={t("booking.monthFull")}
                bookedUntilMessage={bookedUntil}
                noTimesMessage={t("booking.noTimes")}
                firstAvailableLabel={t("booking.firstAvailable")}
                chooseDayMessage={t("booking.chooseDay")}
                zoneLabel={t("internationalPatients.intake.zoneLabel")}
                zoneSearchLabel={t("internationalPatients.intake.zoneSearch")}
                zoneNoResults={t("internationalPatients.intake.zoneNoResults")}
                prevLabel={t("internationalPatients.intake.calendarPrev")}
                nextLabel={t("internationalPatients.intake.calendarNext")}
                callTitle={
                  shownRoute === "surgicalConsult" || introOffered
                    ? ""
                    : t(
                        shownRoute && shownRoute !== "introCall"
                          ? `booking.title.${shownRoute}`
                          : "internationalPatients.intake.callTitle"
                      )
                }
              />
            }
            {chosenSlot && confirmFields}
          </div>
        )}
        {failed && (
          <Typography className={classes.note} style={{ color: "#e2544f" }}>
            {t("booking.failed")}
          </Typography>
        )}
      </div>
    </div>
  )
}

LeadIntakeForm.propTypes = {
  intl: PropTypes.object.isRequired,
  selected: PropTypes.array,
  selectedAreas: PropTypes.array,
  onToggleArea: PropTypes.func,
  allSelected: PropTypes.bool,
  onToggleAll: PropTypes.func,
  preset: PropTypes.oneOf(Object.keys(PRESETS)),
  withQuote: PropTypes.bool,
  theme: PropTypes.oneOf(["dark", "light"]),
}

export default injectIntl(LeadIntakeForm)
